import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { VendorRow } from "_interfaces/vendor.interfaces";

import {
  TransportResourceServiceV1BulkDeleteVendorApiArg as BulkDeleteVendorApiArg,
  TransportResourceServiceV1BulkUpdateVendorStatusApiArg as BulkUpdateVendorStatusApiArg,
  TransportResourceVendorListItem,
  useTransportResourceServiceV1BulkDeleteVendorMutation as useBulkDeleteVendorMutation,
  useTransportResourceServiceV1BulkUpdateVendorStatusMutation as useBulkUpdateVendorStatusMutation,
  useTransportResourceServiceV1ExportVendorsMutation as useExportVendorsMutation,
  useTransportResourceServiceV1GetVendorsQuery,
  vendorApi,
} from "_services/modules/transport-resource/vendor";

import { errorHandler } from "_services/errorHandler";

import { Columns, TableSortState } from "components/table";
import { SelectOption } from "components/select";

import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";

import { useAppTranslation } from "locale/useAppTranslation";

import { toast } from "react-toastify";

import Button from "components/button";
import Icon from "components/icon";
import ActionMenu from "components/shared/action-menu";

import { useNavigate } from "react-router-dom";

import { addVendorPageRouteName } from "../add/add.page";

import {
  detailVendorPageRouteName,
} from "../detail/detail.page";

import { useVendorImport } from "hooks/vendor/useVendorImport";
import { dateTimeFormatter } from "_helper/timeFormatter";

export type { VendorRow } from "_interfaces/vendor.interfaces";

const sortByMap: Record<string, string> = {
  vendorName: "name",
  legalName: "legal_name",
  officialPhone: "phone",
  registeredFleet: "registered_fleet",
  status: "status",
  createdAt: "created_at",
  updatedAt: "updated_at",
};

const normalizeStatus = (
  value?: string,
): "active" | "inactive" =>
  value === "active"
    ? "active"
    : "inactive";

export const mapVendorItemsToOptions = (
  vendors?: TransportResourceVendorListItem[],
): SelectOption[] => {
  if (!vendors) {
    return [];
  }

  return vendors
    .filter(
      (
        vendor,
      ): vendor is TransportResourceVendorListItem & {
        id: string;
      } => Boolean(vendor.id),
    )
    .map((vendor) => ({
      value: vendor.id,
      label:
        vendor.name ??
        vendor.code ??
        vendor.id,
      subtitle: vendor.code,
    }));
};

export const useVendorSelectOptions = (
  defaultLimit = 5,
) => {
  const {
    data: initialData,
    isFetching: isQueryFetching,
  } = useTransportResourceServiceV1GetVendorsQuery({
    page: 1,
    limit: defaultLimit,
    searchBy: "name",
  });

  const [
    triggerGetVendors,
    { isFetching: isLazyFetching },
  ] =
    vendorApi.endpoints.transportResourceServiceV1GetVendors.useLazyQuery();

  const searchSeqRef = useRef(0);

  const initialOptions = useMemo(
    () =>
      mapVendorItemsToOptions(
        initialData?.vendor,
      ),
    [initialData],
  );

  const [
    searchedOptions,
    setSearchedOptions,
  ] = useState<SelectOption[] | null>(null);

  const searchVendors = useCallback(
    async (
      search: string,
    ) => {
      if (!search) {
        setSearchedOptions(null);
        return initialOptions;
      }

      const currentSeq = ++searchSeqRef.current;
      try {
        const response =
          await triggerGetVendors({
            page: 1,
            limit: defaultLimit,
            search:
              search || undefined,
            searchBy: "name",
          }).unwrap();

        const options =
          mapVendorItemsToOptions(
            response.vendor,
          );

        if (currentSeq === searchSeqRef.current) {
          setSearchedOptions(options);
        }

        return options;
      } catch {
        if (currentSeq === searchSeqRef.current) {
          setSearchedOptions([]);
        }
        return [];
      }
    },
    [defaultLimit, initialOptions, triggerGetVendors],
  );

  const vendorOptions = searchedOptions ?? initialOptions;

  return {
    vendorOptions,
    isLoadingVendorOptions:
      isQueryFetching || isLazyFetching,
    onSearchVendors:
      searchVendors,
  };
};

const useVendorList = () => {
  const dispatch =
    useAppDispatch();

  const navigate =
    useNavigate();

  const { t } =
    useAppTranslation(
      "vendor",
    );

  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [resultModal, setResultModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    icon: "success" | "danger" | "warning" | "info";
  }>({
    open: false,
    title: "",
    message: "",
    icon: "success",
  });

  const [bulkAction, setBulkAction] = useState("");
  const [sortState, setSortState] = useState<TableSortState>(null);

  const [
    showDeleteConfirmModal,
    setShowDeleteConfirmModal,
  ] = useState(false);

  const [
    deleteRow,
    setDeleteRow,
  ] =
    useState<VendorRow | null>(
      null,
    );

  const [
    statusToggleRow,
    setStatusToggleRow,
  ] = useState<VendorRow | null>(null);

  const [
    showStatusConfirmModal,
    setShowStatusConfirmModal,
  ] = useState(false);

  const [
    isSubmittingBulkAction,
    setIsSubmittingBulkAction,
  ] = useState(false);

  const {
    data: apiResponse,
    isLoading,
    refetch,
  } =
    useTransportResourceServiceV1GetVendorsQuery(
      {
        page: currentPage,
        limit: rowsPerPage,
        search:
          searchValue ||
          undefined,
        status:
          statusFilter ||
          undefined,
        sortBy: sortState
          ? sortByMap[
          sortState.columnId
          ]
          : undefined,
        order:
          sortState?.direction,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const [
    exportVendors,
    {
      isLoading:
      isExporting,
    },
  ] =
    useExportVendorsMutation();

  const [
    bulkUpdateVendorStatus,
  ] =
    useBulkUpdateVendorStatusMutation();

  const [
    bulkDeleteVendor,
    { isLoading: isDeletingVendor },
  ] =
    useBulkDeleteVendorMutation();

  const vendorImport =
    useVendorImport((response) => {
      const count = response?.imported_count ?? 0;
      setResultModal({
        open: true,
        title: t("import-success-title") || "Import Successful",
        message: t("import-success-message", { count }) || `Successfully imported ${count} vendor records into the system.`,
        icon: "success",
      });
      refetch();
    });

  const {
    data: draftResponse,
  } = useTransportResourceServiceV1GetVendorsQuery({
    page: 1,
    limit: 1,
    status: "Draft",
  });

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title:
          t(
            "page-title-list",
          ),
        hasBackButton:
          false,
      }),
    );
  }, [
    dispatch,
    t,
  ]);

  const handleSelectionChange = (keys: (string | number)[], _rows?: VendorRow[]) => {
    setSelectedKeys(keys);
  };

  const tableData: VendorRow[] =
    useMemo(() => {
      if (
        !apiResponse?.vendor
      ) {
        return [];
      }

      return apiResponse.vendor.map(
        (item) => ({
          id:
            item.id ?? "",
          vendorName:
            item.name ?? "-",
          vendorCode:
            item.code ?? "-",
          legalName:
            item.legal_name ??
            "-",
          officialPhone:
            item.phone ?? "-",
          registeredFleet:
            item.registered_fleet ??
            0,
          status:
            normalizeStatus(
              item.status,
            ),
          createdBy:
            item.created_by?.name ?? "-",
          createdAt:
            item.created_by?.timestamp
              ? dateTimeFormatter(item.created_by.timestamp)
              : "-",
          updatedBy:
            item.updated_by?.name || item.created_by?.name || "-",
          updatedAt:
            item.updated_by?.timestamp
              ? dateTimeFormatter(item.updated_by.timestamp)
              : item.created_by?.timestamp
              ? dateTimeFormatter(item.created_by.timestamp)
              : undefined,
        }),
      );
    }, [apiResponse]);

  const totalPages =
    apiResponse?.metadata
      ?.total_page ?? 1;

  const [isExportOpen, setIsExportOpen] = useState(false);
  const [fileFormat, setFileFormat] = useState("xlsx");
  const [exportType, setExportType] = useState("all");

  const exportFileFormatOptions = [
    {
      label: "Excel (.xlsx)",
      value: "xlsx",
    },
    {
      label: "CSV (.csv)",
      value: "csv",
    },
  ];

  const exportTypeOptions = [
    {
      label: "All Data",
      value: "all",
    },
    {
      label: "Selected Data",
      value: "selected",
    },
  ];

  const getTranslation = (
    key: string,
    fallback: string,
    options?: Record<string, unknown>,
  ) => {
    const val = t(key as never, options);

    if (val === key) {
      if (options && typeof options === "object") {
        let resolvedFallback = fallback;

        Object.entries(options).forEach(([k, v]) => {
          resolvedFallback = resolvedFallback.replace(
            new RegExp(`\\{\\{${k}\\}\\}`, "g"),
            String(v),
          );
        });

        return resolvedFallback;
      }

      return fallback;
    }

    return val;
  };

  const handleExportSubmit = async () => {
    if (!fileFormat) {
      toast.error("Please select file format");
      return;
    }

    if (!exportType) {
      toast.error("Please select export type");
      return;
    }

    if (exportType === "selected" && selectedKeys.length === 0) {
      toast.error("No vendor selected");
      return;
    }

    try {
      const blob = await exportVendors({
        transportResourceExportVendorsReq: {
          format: fileFormat,
          scope: exportType,
          ids: exportType === "selected" ? (selectedKeys as string[]) : undefined,
          filters: {
            search: searchValue || undefined,
            status: statusFilter || undefined,
            sort_by: sortState ? sortByMap[sortState.columnId] : undefined,
            order: sortState?.direction,
          }
        }
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vendors.${fileFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      const count = exportType === "selected" ? selectedKeys.length : (apiResponse?.metadata?.total ?? 0);
      const msg = searchValue || statusFilter
        ? getTranslation("export-success-message-filtered", "{{count}} filtered vendors data successfully exported", { count })
        : getTranslation("export-success-message", "{{count}} vendors data successfully exported", { count });

      setResultModal({
        open: true,
        title: getTranslation("export-success-title", "Export Successful"),
        message: msg,
        icon: "success",
      });

      setIsExportOpen(false);
      setFileFormat("xlsx");
      setExportType("all");
    } catch (error: any) { // Catch block error defaults to any
      toast.error(error?.data?.message || "Export failed");
    }
  };

  const handleToggleStatusRequest = (
    row: VendorRow,
  ) => {
    setStatusToggleRow(row);
    setShowStatusConfirmModal(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (!statusToggleRow) {
      return;
    }

    try {
      const nextStatus =
        statusToggleRow.status === "active"
          ? "inactive"
          : "active";

      const payload: BulkUpdateVendorStatusApiArg = {
        transportResourceBulkUpdateVendorStatusReq: {
          ids: [statusToggleRow.id],
          status: nextStatus,
        }
      };

      await bulkUpdateVendorStatus(payload).unwrap();

      const title = nextStatus === "active"
        ? getTranslation("bulk-activate-success-title", "Successfully Activated!")
        : getTranslation("bulk-deactivate-success-title", "Successfully Deactivated!");
      const message = nextStatus === "active"
        ? getTranslation("toast-activate-success", "{{name}} successfully activated", { name: statusToggleRow.vendorName })
        : getTranslation("toast-deactivate-success", "{{name}} successfully deactivated", { name: statusToggleRow.vendorName });

      setResultModal({
        open: true,
        title,
        message,
        icon: "success",
      });

      setStatusToggleRow(null);
      setShowStatusConfirmModal(false);
      refetch();
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleDeleteRequest = (
    row: VendorRow,
  ) => {
    if (row.status === "active") {
      toast.error(
        getTranslation(
          "toast-active-vendor-cannot-delete",
          "Active vendor cannot be deleted",
        ),
      );
      return;
    }

    setDeleteRow(row);

    setShowDeleteConfirmModal(
      true,
    );
  };

  const handleConfirmDelete =
    async () => {
      if (!deleteRow || deleteRow.status === "active") {
        return;
      }

      try {
        const payload: BulkDeleteVendorApiArg =
        {
          ids: [
            deleteRow.id,
          ],
        };

        const deleteName = deleteRow.vendorName;

        await bulkDeleteVendor(
          payload,
        ).unwrap();

        setResultModal({
          open: true,
          title: getTranslation("bulk-delete-success-title", "Successfully Deleted!"),
          message: getTranslation("toast-delete-success", "{{name}} successfully deleted", { name: deleteName }),
          icon: "success",
        });

        refetch();
      } catch (error) {
        errorHandler(error);
      } finally {
        setDeleteRow(
          null,
        );

        setShowDeleteConfirmModal(
          false,
        );
      }
    };

  const [
    showBulkConfirmModal,
    setShowBulkConfirmModal,
  ] = useState(false);

  const selectedStatuses = useMemo(() => {
    const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
    return Array.from(new Set(selectedRows.map((row) => row.status)));
  }, [selectedKeys, tableData]);

  const bulkActionOptions = useMemo(() => {
    if (selectedKeys.length === 0) return [];

    const hasActive = selectedStatuses.includes("active");
    const hasInactive = selectedStatuses.includes("inactive");

    if (selectedStatuses.length === 1) {
      if (hasActive) {
        return [
          {
            label: t("btn-deactivate") || getTranslation("btn-deactivate", "Deactivate"),
            value: "inactive",
          },
        ];
      }
      if (hasInactive) {
        return [
          {
            label: t("btn-activate") || getTranslation("btn-activate", "Activate"),
            value: "active",
          },
          {
            label: t("btn-delete") || getTranslation("btn-delete", "Delete"),
            value: "delete",
          },
        ];
      }
    }

    return [];
  }, [selectedKeys.length, selectedStatuses, t, getTranslation]);

  const effectiveBulkAction = bulkActionOptions.some((opt) => opt.value === bulkAction) ? bulkAction : "";

  const selectedVendorNames = useMemo(() => {
    return selectedKeys
      .map((key) => {
        const row = tableData.find((r) => r.id === key);
        return row ? row.vendorName : "";
      })
      .filter(Boolean);
  }, [selectedKeys, tableData]);

  const handleApplyBulkAction = () => {
    if (
      selectedKeys.length === 0 ||
      !bulkAction
    ) {
      return;
    }
    if (selectedStatuses.length > 1) {
      return;
    }
    if (bulkAction === "active" && selectedStatuses.includes("active")) {
      return;
    }
    if (bulkAction === "inactive" && selectedStatuses.includes("inactive")) {
      return;
    }
    if (bulkAction === "delete") {
      const hasActiveVendor = selectedKeys.some((key) => {
        const row = tableData.find((r) => r.id === key);
        return row?.status === "active";
      });
      if (hasActiveVendor) {
        toast.error(
          getTranslation(
            "toast-cannot-delete-vendor-status",
            "Only inactive vendors can be deleted",
          ),
        );
        return;
      }
    }
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction =
    async () => {
      if (
        selectedKeys.length ===
        0 ||
        !bulkAction
      ) {
        return;
      }

      if (bulkAction === "delete") {
        const hasActiveVendor = selectedKeys.some((key) => {
          const row = tableData.find((r) => r.id === key);
          return row?.status === "active";
        });
        if (hasActiveVendor) {
          toast.error(
            getTranslation(
              "toast-cannot-delete-vendor-status",
              "Only inactive vendors can be deleted",
            ),
          );
          return;
        }
      }

      try {
        setIsSubmittingBulkAction(
          true,
        );

        const count = selectedKeys.length;

        if (
          bulkAction ===
          "active" ||
          bulkAction ===
          "inactive"
        ) {
          const payload: BulkUpdateVendorStatusApiArg =
          {
            transportResourceBulkUpdateVendorStatusReq: {
              ids:
                selectedKeys.map(
                  String,
                ),

              status:
                bulkAction,
            }
          };

          await bulkUpdateVendorStatus(
            payload,
          ).unwrap();

          const title = bulkAction === "active"
            ? getTranslation("bulk-activate-success-title", "Successfully Activated!")
            : getTranslation("bulk-deactivate-success-title", "Successfully Deactivated!");
          const message = bulkAction === "active"
            ? (count === 1
              ? getTranslation("bulk-activate-success-message", "1 Vendor successfully activated", { count })
              : getTranslation("bulk-activate-success-message_plural", "{{count}} Vendors successfully activated", { count }))
            : (count === 1
              ? getTranslation("bulk-deactivate-success-message", "1 Vendor successfully deactivated", { count })
              : getTranslation("bulk-deactivate-success-message_plural", "{{count}} Vendors successfully deactivated", { count }));

          setResultModal({
            open: true,
            title,
            message,
            icon: "success",
          });
        }

        if (
          bulkAction ===
          "delete"
        ) {
          const payload: BulkDeleteVendorApiArg =
          {
            ids:
              selectedKeys.map(
                String,
              ),
          };

          await bulkDeleteVendor(
            payload,
          ).unwrap();

          const message = count === 1
            ? getTranslation("bulk-delete-success-message", "1 Vendor successfully deleted", { count })
            : getTranslation("bulk-delete-success-message_plural", "{{count}} Vendors successfully deleted", { count });

          setResultModal({
            open: true,
            title: getTranslation("bulk-delete-success-title", "Successfully Deleted!"),
            message,
            icon: "success",
          });
        }

        setSelectedKeys([]);
        setBulkAction("");

        refetch();
      } catch (error) {
        errorHandler(error);
      } finally {
        setIsSubmittingBulkAction(
          false,
        );
        setShowBulkConfirmModal(
          false,
        );
      }
    };

  const columns: Columns<VendorRow>[] =
    useMemo(
      () => [
        {
          id: "no",
          fieldId: "index",
          label:
            t("col-no"),
          width: "60px",
          sticky: "left",
        },

        {
          id: "vendorName",
          fieldId: "vendorName",
          label: t("col-vendor"),
          sortable: true,
          render: (row) => (
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold text-text-primary">{row.vendorName}</span>
              {row.vendorCode && row.vendorCode !== "-" && (
                <span className="text-xs text-greyScale-60">{row.vendorCode}</span>
              )}
            </div>
          ),
        },

        {
          id: "legalName",
          fieldId:
            "legalName",
          label:
            t(
              "col-legal-name",
            ),
          sortable: true,
        },

        {
          id: "officialPhone",
          fieldId:
            "officialPhone",
          label:
            t(
              "col-official-phone",
            ),
          sortable: true,
        },

        {
          id: "registeredFleet",
          fieldId:
            "registeredFleet",
          label:
            t(
              "col-registered-fleet",
            ),
          sortable: true,
        },

        {
          id: "status",
          fieldId:
            "status",
          label:
            t(
              "col-status",
            ),
          sortable: true,

          render: (
            row,
          ) => (
            <div className="inline-flex items-center gap-2 text-sm">
              <span
                className={`h-3 w-3 rounded-full ${row.status ===
                  "active"
                  ? "bg-primary-600 border-2 border-primary-100"
                  : "bg-greyScale-60 border-2 border-greyScale-90"
                  }`}
              />

              <span className="capitalize">
                {
                  row.status
                }
              </span>
            </div>
          ),
        },

        {
          id: "createdAt",
          fieldId:
            "createdBy",
          fieldId2:
            "createdAt",
          label:
            t(
              "col-created-by",
            ),
        },

        {
          id: "updatedAt",
          fieldId:
            "updatedBy",
          fieldId2:
            "updatedAt",
          label:
            t(
              "col-updated-by",
            ),
        },

        {
          id: "action",
          fieldId: "id",
          label:
            t(
              "col-action",
            ),
          width: "150px",
          dragDisabled:
            true,
          sticky: "right",

          render: (
            row,
          ) => (
            <div
              className="flex items-center gap-2"
              onClick={(
                event,
              ) =>
                event.stopPropagation()
              }
            >
              <Button
                type="button"
                variant="outline"
                color="primary"
                size="small"
                iconLeft={
                  <Icon
                    name="edit-2"
                    size={14}
                  />
                }
                onClick={() =>
                  navigate(`/master-data/vendor/${row.id}/edit`)
                }
              >
                {t(
                  "btn-edit",
                )}
              </Button>

              <ActionMenu
                items={[
                  {
                    label:
                      row.status ===
                        "active"
                        ? getTranslation(
                          "btn-deactivate",
                          "Deactivate",
                        )
                        : getTranslation(
                          "btn-activate",
                          "Activate",
                        ),

                    onClick:
                      () =>
                        handleToggleStatusRequest(
                          row,
                        ),
                  },

                  ...(row.status !== "active"
                    ? [
                        {
                          label:
                            getTranslation(
                              "btn-delete",
                              "Delete",
                            ),

                          onClick:
                            () =>
                              handleDeleteRequest(
                                row,
                              ),

                          variant:
                            "danger" as const,
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          ),
        },
      ],
      [
        t,
        navigate,
        getTranslation,
        handleToggleStatusRequest,
        handleDeleteRequest,
      ],
    );

  return {
    columns,

    currentPage,

    deleteRow,

    paginatedRows:
      tableData,

    rowsPerPage,

    searchValue,

    selectedKeys,

    bulkAction: effectiveBulkAction,
    bulkActionOptions,

    sortState,

    statusFilter,

    showDeleteConfirmModal,

    isLoading,

    isExporting,

    isDeletingVendor,

    isSubmittingBulkAction,

    totalPages,

    totalSelected:
      selectedKeys.length,

    totalVisibleData:
      apiResponse?.metadata?.total ?? 0,
    draftCount:
      draftResponse?.metadata?.total ?? 0,

    vendorImport,

    t,

    setCurrentPage: (page: number | ((prev: number) => number)) => {
      setSelectedKeys([]);
      setCurrentPage(page);
    },

    setRowsPerPage: (rows: number | ((prev: number) => number)) => {
      setSelectedKeys([]);
      setRowsPerPage(rows);
    },

    setSearchValue: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setSearchValue(val);
    },

    setSelectedKeys,

    handleSelectionChange,

    setBulkAction,

    setShowDeleteConfirmModal,

    setSortState: (sort: TableSortState | ((prev: TableSortState) => TableSortState)) => {
      setSelectedKeys([]);
      setSortState(sort);
    },

    setStatusFilter: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setStatusFilter(val);
    },

    onAddVendor: () =>
      navigate(
        addVendorPageRouteName,
      ),

    onDraft: () =>
      navigate("/master-data/vendor/draft"),

    onExport: () => {
      setExportType(selectedKeys.length > 0 ? "selected" : "all");
      setIsExportOpen(true);
    },

    isExportOpen,
    setIsExportOpen,
    fileFormat,
    setFileFormat,
    exportType,
    setExportType,
    exportFileFormatOptions,
    exportTypeOptions,
    handleExportSubmit,

    resultModal,
    setResultModal,

    statusToggleRow,
    showStatusConfirmModal,
    setShowStatusConfirmModal,
    onConfirmToggleStatus: handleConfirmToggleStatus,
    getTranslation,

    onImport:
      vendorImport.openModal,

    onConfirmDelete:
      handleConfirmDelete,

    handleApplyBulkAction,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    selectedVendorNames,
    onConfirmBulkStatusUpdate: handleConfirmBulkAction,

    onRowClick: (
      row: VendorRow,
    ) =>
      navigate(
        detailVendorPageRouteName(
          row.id,
        ),
      ),
  };
};

export default useVendorList;